import {
  MenuItemOptionGroup,
  OnlineMenu,
  OnlineMenuItem,
  Order,
  Pricing,
  Cart,
  MealPeriod,
} from "@dining/api-client";
import { MenuCategory, categoryOthers } from "./constants";
import { v4 as uuid } from "uuid";
export class HelperUtils {
  private static helperUtils: HelperUtils = new HelperUtils();

  private constructor() {
    HelperUtils.helperUtils = this;
  }

  public static getInstance(): HelperUtils {
    return HelperUtils.helperUtils;
  }

  getAllergenStr = (menuItem: OnlineMenuItem): string => {
    const allergenStr = menuItem.allergens
      ?.map((al) => al.name || "")
      .filter((n) => !!n)
      .join(", ");

    return allergenStr ? "*Contains: " + allergenStr : "";
  };

  private static readonly DIETARY_MAP: Record<string, { short: string; long: string }> = {
    vegan: { short: "(vg)", long: "Vegan" },
    vegetarian: { short: "(v)", long: "Vegetarian" },
  };

  private findDietaryEntry = (menuItem: { dietaryLabels?: string[] }) => {
    const labels = menuItem.dietaryLabels;
    if (!labels || labels.length === 0) return undefined;
    const normalized = labels.map((l) => l.trim().toLowerCase());
    for (const key of Object.keys(HelperUtils.DIETARY_MAP)) {
      if (normalized.includes(key)) return HelperUtils.DIETARY_MAP[key];
    }
    return undefined;
  };

  getDietaryLabelStr = (menuItem: { dietaryLabels?: string[] }): string => {
    return this.findDietaryEntry(menuItem)?.short ?? "";
  };

  getDietaryLabelAccessibleStr = (menuItem: { dietaryLabels?: string[] }): string => {
    return this.findDietaryEntry(menuItem)?.long ?? "";
  };

  getPricingStr = (pricing?: Pricing): string => {
    let pricingStr: string = "";
    pricingStr = pricing
      ? pricing.currencyUnit && pricing.amount
        ? pricing.currencyUnit + pricing.amount?.toFixed(2) + " "
        : ""
      : "";

    return pricingStr;
  };

  getCalorieStr = (menuItem: OnlineMenuItem): string => {
    let calorieStr: string = "";
    calorieStr =
      menuItem.calories != undefined && menuItem.calories != null && menuItem.calories > -1
        ? `${menuItem.calories} Cal`
        : "";

    return calorieStr;
  };

  convertMealPeriodsToStringArray = (mealPeriods: MealPeriod[]): string[] => {
    const mealPeriodStrings: string[] = [];
    mealPeriods?.forEach((mealPeriod) => {
      mealPeriod.name &&
        mealPeriod.hoursOfOperation &&
        mealPeriodStrings.push(mealPeriod?.name + ": " + mealPeriod?.hoursOfOperation);
    });
    return mealPeriodStrings;
  };

  public getFilteredGroupedCafeMenusForSelectedDate = (
    cafeMenusOnSelectedDate: OnlineMenu[],
    stationName: string,
  ): MenuCategory[] => {
    const filteredGroupedMenu: MenuCategory[] = [];
    //Filter menu based on station name
    cafeMenusOnSelectedDate = cafeMenusOnSelectedDate.filter(
      (item) => item.stationName?.toLowerCase() === stationName.toLowerCase(),
    );

    cafeMenusOnSelectedDate?.forEach((menu: OnlineMenu) => {
      for (const item of menu.items!) {
        // If the Agilysys menu item is not 'canPurchaseOnline' then do not include it on the menuItem list
        if (item.vendorId === "Agilysys" && !item.canPurchaseOnline) {
          continue;
        }

        // Find the item's category, and interpret it as an empty string if it's null
        let itemCategory: string = item.category || "";
        let categoryNames: string[] = [];
        categoryNames = itemCategory.trim().split(";");

        categoryNames.forEach(function (categoryName) {
          itemCategory = categoryName;

          //If itemCategory is an empty string or a space(s) then put those items in "Others"
          if (itemCategory.trim().length == 0) {
            itemCategory = categoryOthers;
          }

          // Find the index of the category in the sortedMenu array
          const categoryIndex = filteredGroupedMenu.findIndex(
            (category) => category.name === itemCategory,
          );

          // If the category doesn't exist in the sortedMenu array, add it
          categoryIndex === -1
            ? filteredGroupedMenu.push({
                id: uuid(),
                name: itemCategory,
                items: [item],
              })
            : // Otherwise, add the item to the existing category
              filteredGroupedMenu[categoryIndex].items.push(item);
        });
      }
    });
    return filteredGroupedMenu;
  };

  //Gives flattened menuoption group options as a string
  getSelectedOptionsString = (optionGroups?: MenuItemOptionGroup[]): string => {
    const optionStr: string =
      optionGroups
        ?.map(
          (optionGroup) =>
            optionGroup.options
              ?.map((option) => (option && option.isSelected ? option.name : ""))
              .filter((n) => !!n)
              .join(", ") || "",
        )
        .filter((m) => !!m)
        .join(", ") || "";
    return optionStr;
  };

  getOrderPickupCafeStationsString = (order: Order) => {
    const cafeStr = order?.cafeName ? order?.cafeName : "";

    const stationsStr =
      order?.items
        ?.map((item) => item?.stationName || "")
        .filter((n) => !!n)
        .join(", ") || "";

    return cafeStr + (cafeStr.length > 0 && stationsStr.length > 0 ? ": " : "") + stationsStr;
  };

  getUniqueFormattedStationNames = (cart: Cart) => {
    const stationNames = [...new Set(cart?.items?.map((item) => item?.stationName || ""))];

    const stationNamesStr: string =
      stationNames.length > 1
        ? stationNames.slice(0, -1).join(", ") + " & " + stationNames.slice(-1)
        : stationNames[0];

    return stationNamesStr
      ? stationNamesStr
          .trim()
          .replace(/^&+|&+$/g, "")
          .trim()
      : "";
  };

  getLastThreeDigitsOfNum = (num: string | undefined) => {
    const lastThreeDigits = num?.length && num?.length > 2 ? num?.substring(num.length - 3) : num;
    return lastThreeDigits;
  };

  getDateFromTimestamp = (timestamp?: string | undefined) => {
    if (!timestamp) {
      return "Invalid date";
    }
    const date = new Date(timestamp);
    return `${date.toLocaleString("default", {
      month: "short",
    })} ${date.getDate()}, ${date.getFullYear()}`;
  };
}
